import { Scene } from '@babylonjs/core/scene';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { PhysicsBody } from '@babylonjs/core/Physics/v2/physicsBody';
import { Ability } from '../../systems/AbilitySystem';
import { gameEvents } from '../../core/EventBus';
import { GameEvents } from '../../config/events';
import { COLORS, PLAYER } from '../../config/GameConfig';

/**
 * Overclocked Leap — Basil channels kinetic energy from his pneumatic harness.
 *
 * Two modes:
 *   1. **Super Jump** — A powerful vertical leap (2.5x normal jump height).
 *      Activated when Basil is grounded and no heavy object is nearby.
 *   2. **Object Launch** — Push/launch a massive object that normal interaction
 *      cannot move. Activated when Basil is near a tagged heavy object.
 *
 * Visuals:
 *   - Harness glows bright copper/orange during charge-up
 *   - Steam burst particles from leg brace on release
 *   - Trail particles during the leap arc
 *
 * Cooldown: 8 seconds.
 */
/**
 * Wounds/Medicine tuning hook. See HarnessWoundSystem.
 * Returning `lockedOut: true` causes `activate()` to refuse the press
 * outright (Limping state).
 */
export interface OverclockedLeapTuning {
  drainScale: number;
  cooldownScale: number;
  lockedOut: boolean;
}

export class OverclockedLeap implements Ability {
  readonly id = 'overclockedLeap';
  readonly name = 'Overclocked Leap';
  isActive = false;
  cooldownRemaining = 0;
  readonly cooldownDuration = 8; // seconds

  private scene: Scene;
  private basilMesh: AbstractMesh;
  private physicsBody: PhysicsBody | null = null;
  private tuningProvider: (() => OverclockedLeapTuning) | null = null;

  /** Multiplier applied to normal jump impulse */
  private readonly leapMultiplier = 2.5;

  /** Force applied to heavy objects when launching them */
  private readonly objectLaunchForce = 25;

  /** Charge-up duration before the leap fires (seconds) */
  private readonly chargeTime = 0.35;
  private chargeTimer = 0;
  private isCharging = false;

  /** Trail particles emitted during the leap */
  private trailParticles: ParticleSystem | null = null;

  /** Steam burst particles on release */
  private burstParticles: ParticleSystem | null = null;

  /** Glow light during charge-up */
  private chargeLight: PointLight | null = null;

  /** Optional nearby heavy object to launch instead of jumping */
  private targetObject: AbstractMesh | null = null;
  private targetPhysicsBody: PhysicsBody | null = null;

  /** Track whether we've fired the leap this activation */
  private hasFired = false;

  constructor(scene: Scene, basilMesh: AbstractMesh, physicsBody?: PhysicsBody) {
    this.scene = scene;
    this.basilMesh = basilMesh;
    this.physicsBody = physicsBody ?? null;
  }

  /**
   * Set the physics body reference if not provided in constructor.
   */
  setPhysicsBody(body: PhysicsBody): void {
    this.physicsBody = body;
  }

  /**
   * Resolve the live physics body for Basil's capsule.
   *
   * Prefers the mesh's current `physicsBody` (Babylon sets this as a
   * side effect of `new PhysicsAggregate(mesh, ...)`) when the cached
   * reference has been disposed or the mesh has been re-aggregated.
   * Falls back to the cached reference otherwise.
   *
   * Why this matters: the dev `__startAtPuzzle` hook rebuilds the entire
   * scene and re-constructs Basil + its abilities. In theory the fresh
   * abilities hold fresh body refs, but the teleport flow (Havok
   * disablePreStep toggle + velocity zeroing on the live body) has to
   * address the SAME object the next `setLinearVelocity` call will
   * target. If we ever end up with a cached body whose aggregate has
   * been disposed (seen in rare cross-level boot races when a trace
   * fires `4` immediately after startAtPuzzle resolves), silent no-op
   * is the symptom — `ability:activated` emits, banter plays, cooldown
   * starts, but the capsule never leaves the ground. Reading from
   * `basilMesh.physicsBody` each activation keeps the ability tolerant
   * of that race without changing gameplay tuning.
   */
  private getLiveBody(): PhysicsBody | null {
    const meshBody = this.basilMesh.physicsBody ?? null;
    const cachedDisposed = this.physicsBody?.isDisposed === true;
    if (meshBody && (!this.physicsBody || cachedDisposed || meshBody !== this.physicsBody)) {
      this.physicsBody = meshBody;
    }
    return this.physicsBody;
  }

  /**
   * Set a nearby heavy object that will be launched instead of Basil jumping.
   * Call this from the game logic when Basil is near a launchable object.
   * Pass null to clear the target.
   */
  setTargetObject(mesh: AbstractMesh | null, body: PhysicsBody | null): void {
    this.targetObject = mesh;
    this.targetPhysicsBody = body;
  }

  /**
   * Install a dynamic tuning provider (Wounds/Medicine hook). Read on
   * every {@link activate} call; an active leap keeps its original
   * cooldown/drain to avoid mid-arc tuning churn.
   */
  setTuningProvider(provider: (() => OverclockedLeapTuning) | null): void {
    this.tuningProvider = provider;
  }

  private getTuning(): OverclockedLeapTuning {
    return (
      this.tuningProvider?.() ?? {
        drainScale: 1,
        cooldownScale: 1,
        lockedOut: false,
      }
    );
  }

  canActivate(): boolean {
    if (this.getTuning().lockedOut) return false;
    return !this.isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.isActive = true;
    this.isCharging = true;
    this.chargeTimer = 0;
    this.hasFired = false;

    // Start charge-up visuals
    this.createChargeVisuals();

    // Emit charge-up audio event
    gameEvents.emit(GameEvents.OVERCLOCKED_LEAP_CHARGING, {
      position: this.basilMesh.position.clone(),
    });

    return true;
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    if (this.isCharging) {
      this.chargeTimer += deltaTime;

      // Pulse the charge light intensity
      if (this.chargeLight) {
        const progress = Math.min(this.chargeTimer / this.chargeTime, 1);
        this.chargeLight.intensity = 2 + progress * 4;
        this.chargeLight.range = 3 + progress * 3;
      }

      // Fire when charge is complete
      if (this.chargeTimer >= this.chargeTime) {
        this.fire();
      }
    } else if (this.hasFired) {
      // After firing, keep trail particles alive briefly then deactivate
      // The trail auto-disposes; we deactivate after a short post-fire window
      this.chargeTimer += deltaTime;
      if (this.chargeTimer > 0.6) {
        this.deactivate();
      }
    }
  }

  deactivate(): void {
    this.isActive = false;
    this.isCharging = false;
    // Apply harness cooldown scaling at deactivate time so a wounded
    // Basil has a longer recovery window between leaps.
    const scale = this.getTuning().cooldownScale;
    this.cooldownRemaining = this.cooldownDuration * scale;

    this.disposeVisuals();
  }

  /**
   * Current drain scale applied by the Wounds/Medicine system. The
   * BaseLevel energy gate reads this to apply the tuned cost on
   * activation.
   */
  get drainScale(): number {
    return this.getTuning().drainScale;
  }

  /**
   * Fire the leap or object launch.
   */
  private fire(): void {
    this.isCharging = false;
    this.hasFired = true;
    this.chargeTimer = 0;

    // Dispose charge visuals, create burst
    this.disposeChargeVisuals();
    this.createBurstParticles();

    if (this.targetObject && this.targetPhysicsBody) {
      // Mode 2: Launch a heavy object
      this.launchObject();
    } else {
      // Mode 1: Super jump
      this.superJump();
    }

    // Create trail particles
    this.createTrailParticles();

    // Emit fired event for audio/VFX systems
    gameEvents.emit(GameEvents.OVERCLOCKED_LEAP_FIRED, {
      position: this.basilMesh.position.clone(),
      mode: this.targetObject ? 'launch' : 'jump',
    });
  }

  /**
   * Apply a powerful upward impulse to Basil.
   */
  private superJump(): void {
    const body = this.getLiveBody();
    if (!body) return;

    const currentVelocity = body.getLinearVelocity();
    const leapVelocity = PLAYER.JUMP_IMPULSE * this.leapMultiplier;

    body.setLinearVelocity(
      new Vector3(currentVelocity.x, leapVelocity, 0)
    );
  }

  /**
   * Launch a nearby heavy object upward and away from Basil.
   */
  private launchObject(): void {
    if (!this.targetPhysicsBody || !this.targetObject) return;

    // Direction: mostly up, slightly away from Basil
    const direction = this.targetObject.position.subtract(this.basilMesh.position);
    direction.y = 0;
    if (direction.length() > 0.01) {
      direction.normalize();
    } else {
      direction.x = 1; // Default to right if directly overlapping
    }

    const launchVector = new Vector3(
      direction.x * this.objectLaunchForce * 0.4,
      this.objectLaunchForce,
      0
    );

    this.targetPhysicsBody.applyImpulse(
      launchVector,
      this.targetObject.position
    );

    // Also give Basil a small kickback
    const body = this.getLiveBody();
    if (body) {
      const kickback = new Vector3(
        -direction.x * 2,
        PLAYER.JUMP_IMPULSE * 0.5,
        0
      );
      body.setLinearVelocity(kickback);
    }
  }

  // ─── Visuals ───────────────────────────────────────────────────────────

  private createChargeVisuals(): void {
    // Copper/orange glow light on harness
    this.chargeLight = new PointLight(
      'overclockedChargeLight',
      Vector3.Zero(),
      this.scene
    );
    this.chargeLight.parent = this.basilMesh;
    this.chargeLight.position = new Vector3(0, -0.3, 0.2); // Near leg brace
    this.chargeLight.diffuse = Color3.FromHexString(COLORS.COPPER);
    this.chargeLight.specular = Color3.FromHexString(COLORS.BRASS);
    this.chargeLight.intensity = 2;
    this.chargeLight.range = 3;
  }

  private disposeChargeVisuals(): void {
    if (this.chargeLight) {
      this.chargeLight.dispose();
      this.chargeLight = null;
    }
  }

  private createBurstParticles(): void {
    this.burstParticles = new ParticleSystem('overclockedBurst', 80, this.scene);
    this.burstParticles.emitter = this.basilMesh;
    this.burstParticles.minSize = 0.08;
    this.burstParticles.maxSize = 0.2;
    this.burstParticles.minLifeTime = 0.2;
    this.burstParticles.maxLifeTime = 0.6;
    this.burstParticles.emitRate = 200;
    this.burstParticles.minEmitPower = 2;
    this.burstParticles.maxEmitPower = 5;
    this.burstParticles.direction1 = new Vector3(-2, -1, -2);
    this.burstParticles.direction2 = new Vector3(2, 0.5, 2);
    this.burstParticles.color1 = new Color4(0.72, 0.45, 0.2, 1);   // Copper
    this.burstParticles.color2 = new Color4(1.0, 0.85, 0.4, 1);    // Bright gold
    this.burstParticles.colorDead = new Color4(0.5, 0.5, 0.5, 0);  // Fades to grey steam
    this.burstParticles.blendMode = ParticleSystem.BLENDMODE_ADD;
    this.burstParticles.createPointEmitter(
      new Vector3(-0.2, -0.5, -0.2),
      new Vector3(0.2, -0.3, 0.2)
    );
    this.burstParticles.targetStopDuration = 0.3;
    this.burstParticles.start();

    // Auto-dispose after burst completes
    setTimeout(() => {
      if (this.burstParticles) {
        this.burstParticles.dispose();
        this.burstParticles = null;
      }
    }, 800);
  }

  private createTrailParticles(): void {
    this.trailParticles = new ParticleSystem('overclockedTrail', 40, this.scene);
    this.trailParticles.emitter = this.basilMesh;
    this.trailParticles.minSize = 0.04;
    this.trailParticles.maxSize = 0.12;
    this.trailParticles.minLifeTime = 0.3;
    this.trailParticles.maxLifeTime = 0.8;
    this.trailParticles.emitRate = 60;
    this.trailParticles.minEmitPower = 0.5;
    this.trailParticles.maxEmitPower = 1.5;
    this.trailParticles.direction1 = new Vector3(-0.5, -1, -0.5);
    this.trailParticles.direction2 = new Vector3(0.5, -0.5, 0.5);
    this.trailParticles.color1 = new Color4(0.77, 0.61, 0.24, 0.8);  // Brass
    this.trailParticles.color2 = new Color4(0.72, 0.45, 0.2, 0.6);   // Copper
    this.trailParticles.colorDead = new Color4(0.3, 0.3, 0.3, 0);
    this.trailParticles.blendMode = ParticleSystem.BLENDMODE_ADD;
    this.trailParticles.createPointEmitter(
      new Vector3(-0.1, -0.5, -0.1),
      new Vector3(0.1, -0.3, 0.1)
    );
    this.trailParticles.targetStopDuration = 0.6;
    this.trailParticles.start();

    // Auto-dispose after trail fades
    setTimeout(() => {
      if (this.trailParticles) {
        this.trailParticles.dispose();
        this.trailParticles = null;
      }
    }, 1200);
  }

  private disposeVisuals(): void {
    this.disposeChargeVisuals();

    if (this.burstParticles) {
      this.burstParticles.stop();
      this.burstParticles.dispose();
      this.burstParticles = null;
    }

    if (this.trailParticles) {
      this.trailParticles.stop();
      this.trailParticles.dispose();
      this.trailParticles = null;
    }
  }
}
